import { motion, type HTMLMotionProps } from 'motion/react'
import { REVEAL_DISTANCE, REVEAL_DURATION, REVEAL_EASE, REVEAL_MARGIN } from './variants'
import { useCompactViewport } from './useMediaQuery'

export type RevealDirection = 'up' | 'down' | 'left' | 'right' | 'none'

export type RevealAmount = number | 'some' | 'all'

export interface ScrollRevealProps extends Omit<HTMLMotionProps<'div'>, 'initial' | 'whileInView' | 'viewport' | 'transition'> {
  children: React.ReactNode
  /** Direction of travel: `up` rises from below, `left` slides in from the right, etc. `none` fades only. */
  direction?: RevealDirection
  /** Seconds to wait before the reveal starts. Use for manual stagger. */
  delay?: number
  /** Travel distance in px. Phone-sized viewports render statically (see below). */
  distance?: number
  /**
   * How much of the element must be in view before it reveals. Defaults to `some` (any part),
   * combined with a bottom margin so the reveal starts once the leading edge is well inside the
   * viewport. A fraction such as 0.3 would never trigger for blocks taller than a phone screen.
   */
  amount?: RevealAmount
  /** Reveal once (default) or every time it enters the viewport. */
  once?: boolean
  duration?: number
}

const offsetFor = (direction: RevealDirection, distance: number) => {
  switch (direction) {
    case 'up':
      return { y: distance }
    case 'down':
      return { y: -distance }
    case 'left':
      return { x: distance }
    case 'right':
      return { x: -distance }
    default:
      return {}
  }
}

/**
 * The single scroll-reveal primitive for the public site.
 * Wrap any block; it fades and slides in when scrolled into view.
 * Reduced motion is honoured globally via <MotionConfig reducedMotion="user">.
 */
export function ScrollReveal({
  children,
  direction = 'up',
  delay = 0,
  distance,
  amount = 'some',
  once = true,
  duration,
  ...rest
}: ScrollRevealProps) {
  const compact = useCompactViewport()

  // Phone-sized viewports do not animate reveals at all. iOS Safari paints one frame of the
  // element at its final state before a JavaScript-started opacity animation begins, which
  // shows as a flicker on every section as it scrolls into view. Static content is the only
  // reliable option there; desktop keeps the full reveal.
  if (compact) {
    return <motion.div {...rest}>{children}</motion.div>
  }

  const travel = distance ?? REVEAL_DISTANCE
  const time = duration ?? REVEAL_DURATION

  return (
    <motion.div
      initial={{ opacity: 0, ...offsetFor(direction, travel) }}
      whileInView={{ opacity: 1, x: 0, y: 0 }}
      viewport={{ once, amount, margin: REVEAL_MARGIN }}
      transition={{ duration: time, delay, ease: REVEAL_EASE }}
      {...rest}
    >
      {children}
    </motion.div>
  )
}
